import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import ProductCard from "../../components/storefront/ProductCard";
import { api } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Filter, X, ChevronDown, ArrowRight } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { toggleWishlist, getGuestWishlist } from "../../lib/Wishlist";

const CATEGORIES = ["Necklace", "Mangalsutra", "Rings", "Earrings", "Chains", "Bangles", "Pendant", "Anklets"];
const PURITIES = ["24K", "22K", "20K", "18K", "Silver"];
const METALS = ["Gold", "Silver"];
const GENDERS = ["Women", "Men", "Kids", "Unisex"];
const FIXED_OCCASIONS = ["Wedding", "Festival", "Daily", "Gifting"];
const PRICE_BANDS = [
  { label: "< ₹25,000", min: "", max: "25000" },
  { label: "₹25,000 - ₹50,000", min: "25000", max: "50000" },
  { label: "₹50,000 - ₹1,00,000", min: "50000", max: "100000" },
  { label: "₹1,00,000+", min: "100000", max: "" },
];

function Section({ title, children, count = 0 }) {
  // All sections start collapsed — nothing is pre-selected or pre-expanded.
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line/70 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="filter-section-head w-full flex items-center justify-between py-4 px-2 text-left"
      >
        <span className="font-display text-base font-semibold tracking-wide text-ink flex items-center gap-2">
          {title}
          {count > 0 && <span className="bg-maroon text-pearl text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
        </span>
        <ChevronDown size={16} className={`text-ash filter-chevron ${open ? "open" : ""}`} />
      </button>
      <div className={`filter-section-body ${open ? "open" : ""}`}>
        <div className="pb-4 px-2 space-y-1">{children}</div>
      </div>
    </div>
  );
}

function Radio({ label, checked, onChange, testid, idx = 0 }) {
  return (
    <label
      className="filter-row filter-option flex items-center gap-2.5 text-sm cursor-pointer py-2 px-2"
      style={{ animationDelay: `${Math.min(idx * 45, 360)}ms` }}
      data-testid={testid}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-maroon w-4 h-4" />
      <span className={checked ? "text-maroon font-medium" : ""}>{label}</span>
    </label>
  );
}

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [segments, setSegments] = useState([]);
  const [availableCategories, setAvailableCategories] = useState(null); // per-occasion jewellery types

  // Overview sections — only rendered on the three "browse everything" landing
  // pages: All Jewellery, All Wedding Jewellery, All Occasions. Any of these
  // automatically pick up new categories / segments / occasions the moment
  // they're added elsewhere (admin, or a new product category), since they're
  // built off the live distinct values rather than a hardcoded list.
  const [jewelleryOverviewSections, setJewelleryOverviewSections] = useState(null);
  const [weddingOverviewSections, setWeddingOverviewSections] = useState(null);
  const [occasionsOverviewSections, setOccasionsOverviewSections] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/api/occasions").then((r) => setOccasions(r.data || [])).catch(() => {});
    api.get("/api/segments").then((r) => setSegments(r.data || [])).catch(() => {});
  }, []);

  // When an occasion is picked (e.g. Birthday), only the jewellery types that
  // were marked (while adding the product) as displayable under it are shown.
  const selectedOccasion = params.get("occasion") || "";
  useEffect(() => {
    if (!selectedOccasion) { setAvailableCategories(null); return; }
    api.get(`/api/products/facets?occasion=${encodeURIComponent(selectedOccasion)}`)
      .then((r) => setAvailableCategories(r.data?.categories || []))
      .catch(() => setAvailableCategories(null));
  }, [selectedOccasion]);

  // Fixed + admin occasions merged (dedup)
  const allOccasions = [
    ...FIXED_OCCASIONS,
    ...occasions.map((o) => o.name).filter((n) => !FIXED_OCCASIONS.some((f) => f.toLowerCase() === n.toLowerCase())),
  ];

  const filters = {
    q: params.get("q") || "",
    category: params.get("category") || "",
    family: params.get("family") || "",   // live-inventory main category (ItemName)
    design: params.get("design") || "",   // live-inventory design (Category)
    metal: params.get("metal") || "",
    purity: params.get("purity") || "",
    gender: params.get("gender") || "",
    occasion: params.get("occasion") || "",
    segment: params.get("segment") || "",
    is_bestseller: params.get("is_bestseller") || "",
    sort: params.get("sort") || "",  // no sort pre-selected — filters start fully clear
    min_weight: params.get("min_weight") || "",
    max_weight: params.get("max_weight") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
  };

  // ---------- Overview mode detection ----------
  // "All Jewellery" overview: bare /catalog, nothing selected yet.
  const isJewelleryOverview =
    !filters.category && !filters.family && !filters.design && !filters.occasion && !filters.segment && !filters.q &&
    !filters.is_bestseller && !filters.metal && !filters.purity && !filters.gender &&
    !filters.min_weight && !filters.max_weight && !filters.min_price && !filters.max_price;

  // "All Wedding Jewellery" overview: occasion=Wedding picked, but no specific segment yet.
  const isWeddingOverview =
    filters.occasion === "Wedding" && !filters.segment && !filters.q &&
    !filters.metal && !filters.purity && !filters.gender &&
    !filters.min_weight && !filters.max_weight && !filters.min_price && !filters.max_price;

  // "All Occasions" overview: reached via a dedicated ?view=occasions link (nav mega menu).
  const isOccasionsOverview = (params.get("view") || "") === "occasions";

  const isOverview = isOccasionsOverview || isJewelleryOverview || isWeddingOverview;

  // Fetches up to 6 best-selling (falling back to newest) items per section.
  const buildOverviewSections = async (defs) => {
    const results = await Promise.all(defs.map(async (d) => {
      try {
        const { data } = await api.get(`/api/products?${d.query}&limit=20`);
        const list = (data.items || []).slice().sort((a, b) => (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0));
        return { ...d, items: list.slice(0, 6) };
      } catch {
        return { ...d, items: [] };
      }
    }));
    return results.filter((r) => r.items.length > 0);
  };

  useEffect(() => {
    if (!isJewelleryOverview) return;
    let alive = true;
    setJewelleryOverviewSections(null);
    api.get("/api/products-categories").then(async (r) => {
      const defs = (r.data || []).map((c) => ({
        key: c.category,
        label: c.category,
        query: `category=${encodeURIComponent(c.category)}`,
        linkTo: `/catalog?category=${encodeURIComponent(c.category)}`,
        buttonLabel: `Show more ${c.category}`,
      }));
      const secs = await buildOverviewSections(defs);
      if (alive) setJewelleryOverviewSections(secs);
    }).catch(() => { if (alive) setJewelleryOverviewSections([]); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [isJewelleryOverview]);

  useEffect(() => {
    if (!isWeddingOverview) return;
    if (segments.length === 0) { setWeddingOverviewSections([]); return; }
    let alive = true;
    setWeddingOverviewSections(null);
    const defs = segments.map((s) => ({
      key: s.name,
      label: s.name,
      query: `occasion=Wedding&segment=${encodeURIComponent(s.name)}`,
      linkTo: `/catalog?occasion=Wedding&segment=${encodeURIComponent(s.name)}`,
      buttonLabel: `Show more ${s.name} Jewellery`,
    }));
    buildOverviewSections(defs).then((secs) => { if (alive) setWeddingOverviewSections(secs); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [isWeddingOverview, segments]);

  useEffect(() => {
    if (!isOccasionsOverview) return;
    let alive = true;
    setOccasionsOverviewSections(null);
    const names = ["Festival", ...occasions.map((o) => o.name).filter((n) => n.toLowerCase() !== "festival")];
    const defs = names.map((name) => ({
      key: name,
      label: name,
      query: `occasion=${encodeURIComponent(name)}`,
      linkTo: `/catalog?occasion=${encodeURIComponent(name)}`,
      buttonLabel: `All ${name} Jewellery`,
    }));
    buildOverviewSections(defs).then((secs) => { if (alive) setOccasionsOverviewSections(secs); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [isOccasionsOverview, occasions]);

  const overviewSections = isOccasionsOverview
    ? occasionsOverviewSections
    : isWeddingOverview
      ? weddingOverviewSections
      : isJewelleryOverview
        ? jewelleryOverviewSections
        : null;
  const overviewLoading = isOverview && overviewSections === null;

  // Load wishlist state — API for logged in, localStorage for guests
  const loadWishlist = () => {
    if (user) {
      api.get("/api/wishlist").then((r) => setWishlist(new Set((r.data.items || []).map((p) => p.id)))).catch(() => {});
    } else {
      setWishlist(new Set(getGuestWishlist()));
    }
  };

  useEffect(() => {
    if (isOverview) { setLoading(false); loadWishlist(); return; }
    setLoading(true);
    const qp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && k !== "min_price" && k !== "max_price") qp.set(k, v);
    });
    qp.set("limit", "60");
    api.get(`/api/products?${qp}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
    loadWishlist();
    // eslint-disable-next-line
  }, [params, user]);

  // Keep hearts in sync if wishlist changes elsewhere (guest)
  useEffect(() => {
    const h = () => { if (!user) setWishlist(new Set(getGuestWishlist())); };
    window.addEventListener("wishlist-changed", h);
    return () => window.removeEventListener("wishlist-changed", h);
    // eslint-disable-next-line
  }, [user]);

  const setF = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const setPriceBand = (band) => {
    const p = new URLSearchParams(params);
    const same = filters.min_price === band.min && filters.max_price === band.max;
    if (same) { p.delete("min_price"); p.delete("max_price"); }
    else {
      band.min ? p.set("min_price", band.min) : p.delete("min_price");
      band.max ? p.set("max_price", band.max) : p.delete("max_price");
    }
    setParams(p);
  };

  const toggleWish = async (pid) => {
    try {
      const { action } = await toggleWishlist(pid, !!user);
      setWishlist((s) => { const n = new Set(s); action === "added" ? n.add(pid) : n.delete(pid); return n; });
      toast.success(`Wishlist ${action}`);
    } catch { toast.error("Failed"); }
  };

  const clearAll = () => setParams(new URLSearchParams());

  // Price shown on cards is the live calculation (rate × weight + making + wastage + stones + GST)
  const priceOf = (p) => (typeof p.calculated_price === "number" ? p.calculated_price : (typeof p.price === "number" ? p.price : null));
  const visible = useMemo(() => {
    const lo = filters.min_price ? +filters.min_price : null;
    const hi = filters.max_price ? +filters.max_price : null;
    if (lo == null && hi == null) return items;
    return items.filter((p) => {
      const pr = priceOf(p);
      if (pr == null) return true;
      if (lo != null && pr < lo) return false;
      if (hi != null && pr > hi) return false;
      return true;
    });
  }, [items, filters.min_price, filters.max_price]);

  // Guests see only the first 10 pieces per view; login reveals the full range.
  const GUEST_LIMIT = 10;
  const guestLimited = !user && visible.length > GUEST_LIMIT;
  const shown = guestLimited ? visible.slice(0, GUEST_LIMIT) : visible;

  const activeCount = ["category", "metal", "purity", "gender", "occasion", "segment", "min_weight", "max_weight", "min_price", "max_price"]
    .filter((k) => filters[k]).length;

  // Jewellery types narrowed to the selected occasion (from product inputs)
  const visibleCategories = availableCategories === null
    ? CATEGORIES
    : CATEGORIES.filter((c) => availableCategories.some((a) => a.toLowerCase() === c.toLowerCase()));

  const FilterBody = (
    <div>
      <Section title="Price" count={filters.min_price || filters.max_price ? 1 : 0}>
        <div className="grid grid-cols-2 gap-2 pt-1">
          {PRICE_BANDS.map((b, i) => {
            const on = filters.min_price === b.min && filters.max_price === b.max;
            return (
              <button
                key={b.label}
                type="button"
                onClick={() => setPriceBand(b)}
                data-testid={`price-band-${b.label}`}
                style={{ animationDelay: `${i * 45}ms` }}
                className={`filter-pill filter-option text-sm px-3 py-2 ${on ? "on" : ""}`}
              >
                {b.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-ash mt-3 filter-option" style={{ animationDelay: "200ms" }}>For a custom range, use the inputs below</div>
        <div className="flex items-center gap-2 mt-1 filter-option" style={{ animationDelay: "240ms" }}>
          <input type="number" placeholder="₹ Min" value={filters.min_price} onChange={(e) => setF("min_price", e.target.value)} className="w-full border border-line rounded-full px-4 py-2 text-sm bg-white focus:border-gold outline-none transition-colors" data-testid="filter-min-price" />
          <span className="text-xs text-ash">to</span>
          <input type="number" placeholder="₹ Max" value={filters.max_price} onChange={(e) => setF("max_price", e.target.value)} className="w-full border border-line rounded-full px-4 py-2 text-sm bg-white focus:border-gold outline-none transition-colors" data-testid="filter-max-price" />
        </div>
      </Section>

      <Section title="Occasion" count={filters.occasion ? 1 : 0}>
        {allOccasions.map((c, i) => (
          <Radio key={c} idx={i} label={c} checked={filters.occasion === c} onChange={(v) => setF("occasion", v ? c : "")} testid={`filter-occasion-${c}`} />
        ))}
      </Section>

      <Section title="Jewellery Type" count={filters.category ? 1 : 0}>
        {filters.occasion && availableCategories !== null && (
          <div className="text-[11px] text-ash px-2 pb-1 filter-option">Showing types available for “{filters.occasion}”</div>
        )}
        {visibleCategories.map((c, i) => (
          <Radio key={c} idx={i} label={c} checked={filters.category === c} onChange={(v) => setF("category", v ? c : "")} testid={`filter-category-${c}`} />
        ))}
        {visibleCategories.length === 0 && (
          <div className="text-xs text-ash px-2 py-2 filter-option">No jewellery types available for this occasion yet.</div>
        )}
      </Section>

      <Section title="Gender" count={filters.gender ? 1 : 0}>
        {GENDERS.map((c, i) => (
          <Radio key={c} idx={i} label={c} checked={filters.gender === c} onChange={(v) => setF("gender", v ? c : "")} />
        ))}
      </Section>

      <Section title="Purity" count={filters.purity ? 1 : 0}>
        {PURITIES.map((c, i) => (
          <Radio key={c} idx={i} label={c} checked={filters.purity === c} onChange={(v) => setF("purity", v ? c : "")} />
        ))}
      </Section>

      <Section title="Metal" count={filters.metal ? 1 : 0}>
        {METALS.map((c, i) => (
          <Radio key={c} idx={i} label={c} checked={filters.metal === c} onChange={(v) => setF("metal", v ? c : "")} />
        ))}
      </Section>

      <Section title="Weight (g)" count={filters.min_weight || filters.max_weight ? 1 : 0}>
        <div className="flex gap-2 pt-1 filter-option">
          <input type="number" placeholder="min" value={filters.min_weight} onChange={(e) => setF("min_weight", e.target.value)} className="w-full border border-line rounded-full px-4 py-2 text-sm bg-white focus:border-gold outline-none transition-colors" data-testid="filter-min-weight" />
          <input type="number" placeholder="max" value={filters.max_weight} onChange={(e) => setF("max_weight", e.target.value)} className="w-full border border-line rounded-full px-4 py-2 text-sm bg-white focus:border-gold outline-none transition-colors" data-testid="filter-max-weight" />
        </div>
      </Section>
    </div>
  );

  const overviewEyebrow = isOccasionsOverview ? "Shop by occasion" : isWeddingOverview ? "Wedding jewellery" : "Shop by category";
  const overviewTitle = isOccasionsOverview ? "All Occasions" : isWeddingOverview ? "All Wedding Jewellery" : "All Jewellery";

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10">
        {isOverview ? (
          <>
            <div className="mb-10">
              <div className="eyebrow">{overviewEyebrow}</div>
              <h1 className="font-display text-4xl lg:text-5xl text-maroon mt-1">{overviewTitle}</h1>
            </div>

            {overviewLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-line/40 animate-pulse rounded-xl" />
                ))}
              </div>
            ) : (overviewSections || []).length === 0 ? (
              <div className="text-center py-24 text-ash">No products available yet.</div>
            ) : (
              (overviewSections || []).map((sec) => (
                <section key={sec.key} className="mb-14" data-testid={`overview-section-${sec.key}`}>
                  <h2 className="font-display text-2xl lg:text-3xl text-maroon mb-6">{sec.label}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
                    {sec.items.map((p) => (
                      <ProductCard
                        key={p.id}
                        product={p}
                        price={typeof p.calculated_price === "number" ? p.calculated_price : p.price}
                        onWishlist={toggleWish}
                        isWishlisted={wishlist.has(p.id)}
                      />
                    ))}
                  </div>
                  <div className="mt-6 text-center">
                    <Link
                      to={sec.linkTo}
                      className="inline-flex items-center gap-1 text-maroon underline underline-offset-4 hover:text-gold-dark text-sm"
                      data-testid={`overview-show-more-${sec.key}`}
                    >
                      {sec.buttonLabel} <ArrowRight size={14} />
                    </Link>
                  </div>
                </section>
              ))
            )}
          </>
        ) : (
          <>
            <div className="mb-6">
              <div className="eyebrow">{visible.length} pieces</div>
              <h1 className="font-display text-4xl lg:text-5xl text-maroon mt-1">
                {filters.q
                  ? `Results for "${filters.q}"`
                  : (filters.segment
                      || filters.design
                      || filters.family
                      || filters.category
                      || (filters.occasion === "Wedding" ? "Wedding Jewellery" : filters.occasion)
                      || "All Jewellery")}
              </h1>
            </div>

            {/* Premium floating filter bar */}
            <div className="flex items-center justify-between gap-4 py-3 mb-8">
              <button
                onClick={() => setDrawerOpen(true)}
                className="filter-trigger inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium"
                data-testid="open-filters"
              >
                <Filter size={15} className="text-maroon" /> Filter
                {activeCount > 0 && (
                  <span className="ml-1 bg-maroon text-pearl text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{activeCount}</span>
                )}
              </button>

              <div className="flex items-center gap-2">
                <Select value={filters.sort} onValueChange={(v) => setF("sort", v)}>
                  <SelectTrigger className="filter-trigger w-44 h-10 text-sm border-line" data-testid="sort-select"><SelectValue placeholder="Sort By"/></SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="weight_asc">Weight: Light → Heavy</SelectItem>
                    <SelectItem value="weight_desc">Weight: Heavy → Light</SelectItem>
                    <SelectItem value="price_asc">Price: Low → High</SelectItem>
                    <SelectItem value="price_desc">Price: High → Low</SelectItem>
                  </SelectContent>
                </Select>
                {filters.sort && (
                  <button onClick={() => setF("sort", "")} className="text-xs text-ash hover:text-maroon transition-colors" title="Clear sorting">
                    <X size={14}/>
                  </button>
                )}
              </div>
            </div>

            {/* Product grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-line/40 animate-pulse rounded-xl" />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-24 text-ash">No products match these filters.</div>
            ) : (
              <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
                {shown.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    price={priceOf(p) ?? undefined}
                    onWishlist={toggleWish}
                    isWishlisted={wishlist.has(p.id)}
                  />
                ))}
              </div>
              {guestLimited && (
                <div className="mt-8 text-center bg-pearl border border-gold rounded-xl py-8 px-4" data-testid="guest-login-prompt">
                  <div className="font-display text-2xl text-maroon">There's more to explore</div>
                  <p className="text-sm text-ash mt-2">You're seeing a preview. Please log in to browse our full collection.</p>
                  <Link to="/login" className="btn-maroon mt-4 inline-flex" data-testid="guest-login-cta">Login to see all products</Link>
                </div>
              )}
              </>
            )}
          </>
        )}
      </div>

      {/* Premium floating filter panel */}
      {!isOverview && drawerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] filter-backdrop-in" onClick={() => setDrawerOpen(false)}>
          <div
            className="filter-glass filter-float-in absolute top-4 left-4 bottom-4 w-[430px] max-w-[92vw] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line/60">
              <div>
                <div className="eyebrow text-[9px]">Refine</div>
                <div className="font-display text-2xl font-semibold text-maroon">Filter By</div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="close-filters"
                className="w-9 h-9 rounded-full border border-line flex items-center justify-center hover:border-maroon hover:rotate-90 transition-all duration-300"
              >
                <X size={17} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {FilterBody}
            </div>
            <div className="flex items-center gap-3 px-6 py-4 border-t border-line/60 bg-white/70">
              <button onClick={clearAll} className="filter-pill flex-1 border-maroon text-maroon py-3 text-sm font-medium" data-testid="filter-clear">
                Clear Filters
              </button>
              <button onClick={() => setDrawerOpen(false)} className="filter-pill on flex-1 py-3 text-sm font-medium" data-testid="filter-apply">
                Show Results ({visible.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </StorefrontLayout>
  );
}